import React from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'
import PropTypes from 'prop-types'

import { ColorBoxContent, ColorBoxHeader } from '../ui/colorBox'

/**
 * @typedef ColumnDef
 * @prop {string} dataKey
 * @prop {string} displayTitle
 * @prop {*} [renderer]
 */

/**
 * @param {Object} props
 * @param {ColumnDef[]} props.columnDefs
 * @param {string} props.name
 */
function TableHeaderWrapper(props) {
  return (
    <TableHead>
      <TableRow>
        {props.columnDefs.map((col, index) => (
          <TableCell
            key={`${props.name}-header-${col.dataKey}-${index}`}
            sx={{
              backgroundColor: 'transparent',
              color: '#000000'
            }}
          >
            {col.displayTitle}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

TableHeaderWrapper.propTypes = {
  name: PropTypes.string,
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string
    })
  )
}

/**
 *
 * @template T
 * @param {Object} props
 * @param {string} props.name
 * @param {T[]} props.data
 * @param {string[]} props.keys
 * @param {ColumnDef[]} props.colDefs
 */

function TableBodyWrapper(props) {
  const propColDefs = props.colDefs

  const TableCellWrapper = (row, rowIndex, key, index) => {
    const reactKey = `${props.name}-row-${rowIndex}-key-${index}`
    const matchingColDef = propColDefs.find((cdef) => cdef.dataKey === key)
    if (!matchingColDef) {
      return <div key={reactKey} />
    }

    if (matchingColDef.renderer) {
      return <div key={reactKey}>{matchingColDef.renderer(row)}</div>
    } else {
      return <TableCell key={reactKey}>{row[key]}</TableCell>
    }
  }

  return (
    <TableBody>
      {props.data.map((row, rowIndex) => (
        <TableRow key={`${props.name}-row-${rowIndex}`}>
          {props.keys.map((key, keyindex) =>
            TableCellWrapper(row, rowIndex, key, keyindex)
          )}
        </TableRow>
      ))}
    </TableBody>
  )
}

TableBodyWrapper.propTypes = {
  name: PropTypes.string,
  data: PropTypes.array,
  keys: PropTypes.arrayOf(PropTypes.string),
  colDefs: PropTypes.array
}

/**
 * Reusable table within Orbit.
 * @template T
 * @param {Object} props
 * @param {string} props.name - Name of calling component (for key generation)
 * @param {string} props.header
 * @param {T[]} props.data - should be an object, with the keys given in props.keys
 * @param {ColumnDef[]} props.columnDefs
 * @param {*} props.headerComponent
 */
function AbstractTable(props) {
  return (
    <div>
      {props.header && <ColorBoxHeader>{props.header}</ColorBoxHeader>}
      <ColorBoxContent style={{ marginTop: 12 }}>
        {props.headerComponent}
        <Box>
          <Table>
            <TableHeaderWrapper
              name={props.name}
              columnDefs={props.columnDefs}
            />
            <TableBodyWrapper
              name={props.name}
              data={props.data}
              keys={props.columnDefs.map((def) => def.dataKey)}
              colDefs={props.columnDefs}
            />
          </Table>
        </Box>
      </ColorBoxContent>
    </div>
  )
}
AbstractTable.propTypes = {
  name: PropTypes.string,
  header: PropTypes.string,
  headerComponent: PropTypes.element,
  data: PropTypes.arrayOf(PropTypes.object),
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string
    })
  )
}

export default AbstractTable
